import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import {
  retryUnlessNotInYourVersion,
  yourVersionCourseSlug,
} from '@/api/errors';
import type { LessonStep } from '@/courses/LessonContent';
import { LessonContent } from '@/courses/LessonContent';
import { useCourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { sameSlug } from '~/utils/slug';

export const Route = createFileRoute('/_app/_course/lessons/$lessonSlug')({
  component: LessonScreen,
});

function LessonScreen() {
  const { lessonSlug } = Route.useParams();
  const { setSelectedLessonSlug, setExpandedLessons, setSelectedTopicSlug } =
    useCourseContext();
  const { me, isStudent } = useCurrentUser();
  const { t } = useTranslation();
  const language = me?.language ?? 'EN';

  useEffect(() => {
    setSelectedLessonSlug(lessonSlug);
    setExpandedLessons((prev) =>
      prev.includes(lessonSlug) ? prev : [...prev, lessonSlug],
    );
    setSelectedTopicSlug('');
  }, [
    lessonSlug,
    setSelectedLessonSlug,
    setExpandedLessons,
    setSelectedTopicSlug,
  ]);

  const { data: resolved, error: resolveError } = api.lessonBySlug.useQuery(
    { path: { slug: lessonSlug }, query: { language } },
    { retry: retryUnlessNotInYourVersion },
  );

  const lessonId = resolved?.id;

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const { data: topicsData } = api.lessonTopics.useQuery(
    { path: { lesson_id: lessonId ?? 0 }, query: { limit: 50 } },
    { enabled: lessonId != null },
  );

  const courseId = lessonDetail?.course_id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  // Same parameters as the layout's query, so the two share one cache entry.
  // Only the paginated `count` is needed here, as the "of M" denominator.
  const { data: courseLessons } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: courseProgress } = api.courseProgress.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null && isStudent },
  );

  const courseSlug = courseDetail?.slug;

  // A viewer without access is sent to the parent course, as in legacy. Nothing
  // on this screen is access-gated server-side, so the redirect is driven by
  // `course_viewer_state`, which always answers `has_access` true for a
  // non-student (preview mode).
  if (viewerState?.has_access === false && courseSlug) {
    return (
      <Navigate to="/courses/$courseSlug/" params={{ courseSlug }} replace />
    );
  }

  // An old link: the viewer's version of the course has no such lesson.
  const versionCourseSlug = yourVersionCourseSlug(resolveError);
  if (versionCourseSlug) {
    return (
      <Navigate
        to="/courses/$courseSlug/"
        params={{ courseSlug: versionCourseSlug }}
        replace
      />
    );
  }

  // An old link to the same lesson in another version: show the viewer's
  // version's address, which the sidebar and next links use.
  if (resolved && !sameSlug(resolved.slug, lessonSlug)) {
    return (
      <Navigate
        to="/lessons/$lessonSlug/"
        params={{ lessonSlug: resolved.slug }}
        replace
      />
    );
  }

  if (!lessonDetail || !topicsData) {
    return null;
  }

  const lessonProgress = courseProgress
    ? courseProgress[String(lessonId)]
    : undefined;
  // The per-topic completed set, not the `topics_completed` counter: a student
  // can have a gap inside a lesson, which a prefix rule would mark wrongly.
  const completedTopicIds = new Set(lessonProgress?.completed_topic_ids ?? []);

  const orderedTopics = [...(topicsData.data ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const steps: LessonStep[] = orderedTopics.map((topic) => ({
    slug: topic.slug,
    title: topic.title,
    type: 'topic',
    completed: lessonProgress ? completedTopicIds.has(topic.id) : undefined,
  }));

  // `has_final_quiz` means the lesson has a published final-quiz question, not
  // just a quiz row: a lesson whose questions are all drafts would give an
  // empty quiz that scores 0. It can't be derived from `lessonQuestions`
  // either, which 403s until every topic is complete.
  if (lessonDetail.has_final_quiz) {
    // Labelled from the catalogue, since no read on this screen serves the
    // quiz's own title.
    steps.push({ slug: lessonSlug, title: t('lessons.quiz'), type: 'quiz' });
  }

  const isLessonCompleted = lessonProgress?.completed ?? false;

  return (
    <LessonContent
      courseTitle={courseDetail?.title ?? ''}
      courseSlug={courseSlug ?? ''}
      lessonId={lessonId}
      lessonTitle={lessonDetail.title}
      lessonSlug={lessonSlug}
      lessonIndex={lessonDetail.index}
      lessonsTotal={courseLessons?.count ?? 0}
      topics={steps}
      isStudent={isStudent}
      isCompleted={isLessonCompleted}
    />
  );
}
